import { WwLoader } from '@shared/components/WwLoader'
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import type { Asset } from '@shared/types'
import { getAssetRepository } from '@shared/services/assetRepository'
import { planFor, smartFor } from '@shared/services/smartSearchService'
import { libraryTerms, searchStock, type StockPicture } from '@shared/services/stockService'

/**
 * The Mood board page before there is a board (Huanrui, 2026-10-12)
 * -------------------------------------------------------------
 * A question and one search box in the middle of the screen, Visual
 * Language's pictures drifting slowly behind in columns. Under the box, where
 * to look -- All, Library, Unsplash -- as three buttons, then Another 20 and
 * Create mood board with the count picked.
 *
 * A search (the Search button or Enter) brings out TWENTY pictures, packed
 * round the box and drawn in towards it, big and small. The first twenty fly
 * out of the drifting columns, which then fade away; each later twenty takes
 * the place of the last -- the old ones sink back, the new ones open out from
 * behind the box. Pictures are loaded before they move, so nothing pops in
 * mid-flight, and only transform and opacity are animated, so it stays smooth.
 *
 * Click a picture to drop it in the bucket, or press and sweep across several
 * (the first one decides: in or out). The bucket keeps filling across
 * searches; the first in leads.
 *
 * LIBRARY is Visual Language. ALL is Visual Language and Unsplash (once its
 * key is set), taken in turns. Audit and Design References take no part
 * (Huanrui, 2026-10-12). Unsplash pictures are shown from Unsplash's own
 * servers and credited on the board; nothing from them is filed into the
 * library.
 */

export interface SourcePicture {
  /** Unique across the sources: `library:<id>`, `unsplash:<id>`. */
  key: string
  /** 'board': a picture on a project's board, given to the canvas's Mood board card (2026-10-16). */
  source: 'library' | 'unsplash' | 'pexels' | 'board'
  title: string
  thumb: string
  /** What the sheet shows (and, without `full`, what the board is cut from). */
  url: string
  /**
   * The picture at its own size, where `url` is a smaller one to show
   * (Unsplash, Pexels): what is cut, kept and sent (Huanrui, 2026-10-16).
   */
  full?: string
  width: number
  height: number
  /** A library picture's own record. */
  asset?: Asset
  /** A board picture's own reference (imageRefs.ts encodeRef), what it is sent to Miro by. */
  ref?: string
  /** Unsplash (and Pexels, should it come back). */
  credit?: { author: string; authorUrl: string; link: string }
  downloadLocation?: string
}

export function fromAsset(asset: Asset): SourcePicture {
  return {
    key: `library:${asset.id}`,
    source: 'library',
    title: asset.title,
    thumb: asset.thumbnailPath || asset.originalPath,
    url: asset.originalPath || asset.thumbnailPath,
    width: asset.width,
    height: asset.height,
    asset,
  }
}

export function fromStock(picture: StockPicture): SourcePicture {
  return {
    key: `${picture.source}:${picture.id}`,
    source: picture.source,
    title: picture.title,
    thumb: picture.thumb,
    url: picture.url,
    ...(picture.full ? { full: picture.full } : {}),
    width: picture.width,
    height: picture.height,
    credit: { author: picture.author, authorUrl: picture.authorUrl, link: picture.link },
    downloadLocation: picture.downloadLocation,
  }
}

type Where = 'all' | 'library' | 'unsplash'
const WHERE_NAME: Record<Where, string> = { all: 'All', library: 'Library', unsplash: 'Unsplash' }

const SHOWN = 20
/** Visual Language's first answer; Unsplash gives 30 a page. */
const PER_LIBRARY = 60
/** Longest a wave waits for its pictures to load before it moves anyway. */
const PRELOAD_MS = 1500
const OUT_MS = 320

/** A steady pseudo-random number, so a wall does not jump on resize. */
function seeded(n: number): number {
  const x = Math.sin(n * 127.1 + 311.7) * 43758.5453
  return x - Math.floor(x)
}

/** Every source's answers, taken in turns: one from each, then again. */
function interleave(lists: SourcePicture[][]): SourcePicture[] {
  const out: SourcePicture[] = []
  const seen = new Set<string>()
  for (let i = 0; lists.some((list) => i < list.length); i += 1) {
    for (const list of lists) {
      const one = list[i]
      if (one && !seen.has(one.key)) {
        seen.add(one.key)
        out.push(one)
      }
    }
  }
  return out
}

interface Box {
  left: number
  top: number
  width: number
  height: number
}

const hits = (a: Box, b: Box, gap: number) =>
  a.left < b.left + b.width + gap &&
  b.left < a.left + a.width + gap &&
  a.top < b.top + b.height + gap &&
  b.top < a.top + a.height + gap

/**
 * The wall. Big pictures first, each placed at the free spot nearest the
 * middle -- searched outward ring by ring, starting from a different angle
 * each time (the golden angle) so they spread round rather than piling on
 * one side. Nothing overlaps another picture, the box in the middle, the
 * page's edges or the foot kept for the bucket. A picture that finds no room
 * is tried smaller.
 */
function wall(stage: { w: number; h: number }, ratios: number[], centre: Box): Box[] {
  const W = stage.w
  const H = stage.h - 64
  const cx = W / 2
  const cy = stage.h / 2
  const gap = 12
  // Three large, six medium, the rest small: the rhythm of a pinned wall.
  const sizeOf = (rank: number) => (rank < 3 ? 1.12 : rank < 9 ? 0.8 : 0.58) * (0.94 + seeded(rank + 7) * 0.12)
  // Tried a quarter larger (2026-10-12); the wall spread to the edges and
  // lost its shape, so back to this size.
  const unit = Math.max(110, Math.min(W, H) * 0.24)
  const order = ratios.map((_, i) => i)
  const placed: (Box | null)[] = ratios.map(() => null)
  const taken: Box[] = [centre]
  order.forEach((i, rank) => {
    const r = ratios[i]
    let scale = sizeOf(rank)
    for (let attempt = 0; attempt < 5 && !placed[i]; attempt += 1, scale *= 0.85) {
      // The same area whatever the shape, so a panorama is not a giant.
      const height = (unit * scale) / Math.sqrt(r)
      const width = height * r
      const start = rank * 2.39996
      search: for (let ring = 0; ring < 400; ring += 1) {
        const radius = ring * 9
        const steps = Math.max(8, Math.round((2 * Math.PI * radius) / 22))
        for (let s = 0; s < steps; s += 1) {
          const a = start + (s / steps) * Math.PI * 2
          // Wider than tall, like the screen.
          const x = cx + Math.cos(a) * radius * 1.55 - width / 2
          const y = cy + Math.sin(a) * radius - height / 2
          if (x < 14 || y < 14 || x + width > W - 14 || y + height > H) continue
          const box = { left: x, top: y, width, height }
          if (taken.some((other) => hits(box, other, gap))) continue
          placed[i] = box
          taken.push(box)
          break search
        }
        if (radius > Math.max(W, H)) break
      }
    }
  })
  return placed.map((box) => box ?? { left: -9999, top: -9999, width: 0, height: 0 })
}

/** Columns of Visual Language drifting behind the box, half of them upward. */
export function Drift({ pictures, ready, gone }: { pictures: SourcePicture[]; ready: boolean; gone: boolean }) {
  const columns = 7
  /*
   * Only pictures that have loaded are dealt into the columns (2026-10-21:
   * black blocks still showed). Two causes: a picture that had not loaded
   * yet stood as an empty box, and `loading="lazy"` never loaded pictures
   * that the drift carries into view, because the browser judges "near the
   * screen" by where a picture is laid out, not by where the animation has
   * moved it. Now every picture is fetched first; the columns are dealt from
   * those that arrived, once, when all have answered or after 2.5 s, so
   * nothing moves after it has appeared.
   */
  const [usable, setUsable] = useState<SourcePicture[]>([])
  useEffect(() => {
    let stopped = false
    const unique = pictures.filter((one, i) => pictures.findIndex((other) => other.thumb === one.thumb) === i)
    if (unique.length === 0) {
      setUsable([])
      return
    }
    const loaded = new Set<string>()
    let answered = 0
    let dealt = false
    const deal = () => {
      if (stopped || dealt) return
      const have = unique.filter((one) => loaded.has(one.thumb))
      if (have.length === 0) return
      dealt = true
      setUsable(have)
    }
    for (const one of unique) {
      const image = new Image()
      const done = (ok: boolean) => {
        if (ok) loaded.add(one.thumb)
        answered += 1
        if (answered === unique.length) deal()
      }
      image.onload = () => {
        image.decode().then(
          () => done(true),
          () => done(true),
        )
      }
      image.onerror = () => done(false)
      image.src = one.thumb
    }
    const late = window.setTimeout(deal, 2500)
    // Nothing had loaded by then: deal as soon as anything does.
    const retry = window.setInterval(() => {
      if (dealt) window.clearInterval(retry)
      else if (loaded.size > 0) deal()
    }, 500)
    return () => {
      stopped = true
      window.clearTimeout(late)
      window.clearInterval(retry)
    }
  }, [pictures])

  /*
   * Every column at least ten deep (2026-10-14): with few pictures a column
   * ran out before the bottom of the screen. The pictures are dealt round
   * again until each column is full.
   */
  const deep = 10
  const lists = Array.from({ length: columns }, () => [] as SourcePicture[])
  if (usable.length > 0) {
    for (let i = 0; i < Math.max(usable.length, columns * deep); i += 1) {
      lists[i % columns].push(usable[i % usable.length])
    }
  }
  return (
    <div className={`mbx__drift${ready && usable.length > 0 ? ' mbx__drift--in' : ''}${gone ? ' mbx__drift--gone' : ''}`} aria-hidden="true">
      {usable.length > 0
        ? lists.map((list, c) => (
            <div key={c} className={`mbx__col${c % 2 ? ' mbx__col--up' : ''}`} style={{ animationDuration: `${70 + c * 9}s` }}>
              {/* Twice over, so the loop has no seam. Already loaded, so eager. */}
              {[...list, ...list].map((one, i) => (
                <img key={`${one.key}-${i}`} src={one.thumb} alt="" draggable={false} />
              ))}
            </div>
          ))
        : null}
    </div>
  )
}

export function preload(pictures: SourcePicture[], wait = PRELOAD_MS): Promise<void> {
  const each = pictures.map(
    (one) =>
      new Promise<void>((resolve) => {
        const image = new Image()
        image.onload = () => {
          // Decoded too, so the first frame of the flight already has it.
          image.decode().then(resolve, resolve)
        }
        image.onerror = () => resolve()
        image.src = one.thumb
      }),
  )
  return Promise.race([Promise.all(each).then(() => undefined), new Promise<void>((resolve) => setTimeout(resolve, wait))])
}

export function MoodboardStart({
  selected,
  onMake,
}: {
  /** What is selected in the libraries right now. */
  selected: Asset[]
  onMake: (pictures: SourcePicture[], words: string) => void
}) {
  const repository = useMemo(() => getAssetRepository(), [])
  const [query, setQuery] = useState('')
  const [asked, setAsked] = useState('')
  // Visual Language only since 2026-10-27, when Unsplash was taken out (Huanrui).
  const [where, setWhere] = useState<Where>('library')
  const unsplash = false
  const [backdrop, setBackdrop] = useState<SourcePicture[]>([])
  const [pool, setPool] = useState<SourcePicture[]>([])
  const [round, setRound] = useState(0)
  /** What is on the wall now, and how it came on. */
  const [shown, setShown] = useState<{ pictures: SourcePicture[]; wave: number; from: 'drift' | 'centre' }>({
    pictures: [],
    wave: 0,
    from: 'drift',
  })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [bucket, setBucket] = useState<SourcePicture[]>([])
  /** The drifting columns fade in once their first pictures have loaded, instead of flashing up. */
  const [driftReady, setDriftReady] = useState(false)
  const [menu, setMenu] = useState(false)
  const run = useRef(0)

  const stageRef = useRef<HTMLElement | null>(null)
  const centreRef = useRef<HTMLFormElement | null>(null)
  const ring = useRef<HTMLDivElement | null>(null)
  const [stage, setStage] = useState({ w: 1600, h: 820 })
  const [centre, setCentre] = useState<Box>({ left: 520, top: 300, width: 560, height: 220 })
  useEffect(() => {
    const node = stageRef.current
    if (!node) return
    const measure = () => {
      setStage({ w: node.clientWidth, h: node.clientHeight })
      const box = centreRef.current?.getBoundingClientRect()
      const at = node.getBoundingClientRect()
      if (box) setCentre({ left: box.left - at.left - 22, top: box.top - at.top - 20, width: box.width + 44, height: box.height + 40 })
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(node)
    if (centreRef.current) observer.observe(centreRef.current)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    // Visual Language's Modern pictures set the tone (2026-10-12); topped up
    // with the newest if there are too few to fill the columns.
    const DRIFT = 84
    Promise.all([
      repository.search({ text: 'modern', library: 'visual-language', limit: DRIFT }).then((page) => page.items).catch(() => []),
      repository.search({ library: 'visual-language', limit: DRIFT }).then((page) => page.items).catch(() => []),
    ])
      .then(([modern, newest]) => {
        const seen = new Set(modern.map((asset) => asset.id))
        const items = modern.length >= 42 ? modern : [...modern, ...newest.filter((asset) => !seen.has(asset.id))].slice(0, DRIFT)
        const pictures = items.map(fromAsset)
        setBackdrop(pictures)
        // The ones in view first: the columns show only when they are there.
        void preload(pictures.slice(0, 35)).then(() => setDriftReady(true))
      })
      .catch(() => setBackdrop([]))
  }, [repository])

  const places: Where[] = unsplash ? ['all', 'library', 'unsplash'] : ['library']

  // ---------------------------------------------------------- waves
  /**
   * Put these twenty on the wall. The ones there now sink back first; the
   * new ones are loaded before anything moves.
   */
  const present = useCallback(async (next: SourcePicture[]) => {
    const host = ring.current
    const old = host ? [...host.querySelectorAll<HTMLElement>('.mbx__card')] : []
    const first = old.length === 0
    const loading = preload(next)
    if (!first) {
      const out = old.map((card, i) =>
        card.animate(
          [
            { transform: 'translate(0, 0) scale(1)', opacity: 1 },
            { transform: 'translate(0, 6px) scale(0.92)', opacity: 0 },
          ],
          { duration: OUT_MS, delay: Math.min(i, 19) * 8, easing: 'cubic-bezier(0.4, 0, 1, 1)', fill: 'forwards' },
        ).finished,
      )
      await Promise.all([Promise.all(out).catch(() => undefined), loading])
    } else {
      await loading
    }
    setShown((now) => ({ pictures: next, wave: now.wave + 1, from: first ? 'drift' : 'centre' }))
  }, [])

  const show = (list: SourcePicture[], at: number) => {
    if (list.length === 0) return
    const start = (at * SHOWN) % list.length
    const next = [...list.slice(start, start + SHOWN)]
    if (next.length < SHOWN && list.length > SHOWN) next.push(...list.slice(0, SHOWN - next.length))
    void present(next)
  }

  const search = (text = query, place: Where = where) => {
    const q = text.trim()
    if (!q) return
    const mine = ++run.current
    setAsked(q)
    setBusy(true)
    setError('')
    /*
     * Not only the tags typed (2026-10-12: "聪明的搜索而不是严格按照tag"):
     * the words are also read for their meaning against Visual Language's
     * own tags (routes/moodboardTerms.ts), each of those is searched, and a
     * picture ranks by how many it answers -- the words as typed counting
     * most. Without an answer, the words as typed, as before.
     */
    const english = /^[\x00-\x7f]*$/.test(q)
    const meaning = place === 'unsplash' && english ? Promise.resolve([] as string[]) : libraryTerms(q)
    /*
     * And understood as every other search box understands a sentence
     * (2026-11-03): tags, words and the whole phrase against each picture's
     * description, scored. Those come first; nothing for a single word.
     */
    const understood = planFor(q, ['visual-language']).then((plan) => {
      const smart = plan ? smartFor(plan, 'visual-language') : null
      return smart
        ? repository
            .search({ library: 'visual-language', limit: PER_LIBRARY, smart })
            .then((page) => page.items.map(fromAsset))
            .catch(() => [] as SourcePicture[])
        : ([] as SourcePicture[])
    })
    const library = async () => {
      const find = (text: string, limit: number) =>
        repository
          .search({ text, library: 'visual-language', limit })
          .then((page) => page.items.map(fromAsset))
          .catch(() => [] as SourcePicture[])
      const exact = find(q, PER_LIBRARY)
      let meant = await meaning
      const asTyped = await exact
      // Nothing for the words as typed and no meaning yet: worth waiting longer for it.
      if (asTyped.length === 0 && meant.length === 0) meant = await libraryTerms(q, 10_000)
      const terms = meant.filter((term) => term.toLowerCase() !== q.toLowerCase()).slice(0, 3)
      const lists = [asTyped, ...(await Promise.all(terms.map((term) => find(term, 30))))]
      const score = new Map<string, { one: SourcePicture; score: number; first: number }>()
      // The sentence understood counts most, in its own order.
      ;(await understood).forEach((one, at) => score.set(one.key, { one, score: 4, first: -100_000 + at }))
      lists.forEach((list, which) =>
        list.forEach((one, at) => {
          const had = score.get(one.key)
          // The words as typed weigh three; each meaning, less the further down it was offered.
          const worth = which === 0 ? 3 : 1 + (terms.length - which + 1) / (terms.length * 2)
          if (had) had.score += worth
          else score.set(one.key, { one, score: worth, first: which * 1000 + at })
        }),
      )
      return [...score.values()]
        .sort((a, b) => b.score - a.score || a.first - b.first)
        .slice(0, PER_LIBRARY)
        .map((entry) => entry.one)
    }
    // Unsplash reads English best: words in another language go as their meaning.
    const stockWords = async () => {
      if (english) return q
      const terms = await meaning
      return terms.length > 0 ? terms.slice(0, 3).join(' ') : q
    }
    const stock = () =>
      stockWords()
        .then((words) => searchStock('unsplash', words, 1))
        .then((found) => found.pictures.map(fromStock))
        .catch((caught: unknown) => {
          if (mine === run.current) setError(caught instanceof Error ? caught.message : 'Unsplash did not answer')
          return [] as SourcePicture[]
        })
    const asks = place === 'library' ? [library()] : place === 'unsplash' ? [stock()] : unsplash ? [library(), stock()] : [library()]
    void Promise.all(asks)
      .then((lists) => {
        if (mine !== run.current) return
        const found = interleave(lists)
        setPool(found)
        setRound(0)
        if (found.length > 0) show(found, 0)
        else void present([])
      })
      .finally(() => {
        if (mine === run.current) setBusy(false)
      })
  }

  const another = () => {
    const next = round + 1
    setRound(next)
    show(pool, next)
  }
  /** These words, already searched and on the wall: Search has done its work. */
  const fresh = !busy && asked !== '' && query.trim() === asked && pool.length > 0


  const ratios = shown.pictures.map((one) =>
    one.width > 0 && one.height > 0 ? Math.min(1.8, Math.max(0.6, one.width / one.height)) : 0.8,
  )
  const spots = useMemo(() => wall(stage, ratios, centre), [stage, centre, ratios.join(',')]) // eslint-disable-line react-hooks/exhaustive-deps

  /*
   * The entrance. The first wave: each card starts on top of a picture
   * drifting behind, at its size, and travels to its place. Later waves open
   * out from behind the box. Transform and opacity only.
   */
  useLayoutEffect(() => {
    const host = ring.current
    if (!host || shown.pictures.length === 0) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    const from =
      shown.from === 'drift'
        ? [...document.querySelectorAll<HTMLImageElement>('.mbx__col img')]
            .map((img) => img.getBoundingClientRect())
            .filter((r) => r.width > 20 && r.bottom > 0 && r.top < window.innerHeight)
        : []
    const middle = centreRef.current?.getBoundingClientRect()
    ;[...host.querySelectorAll<HTMLElement>('.mbx__card')].forEach((card, i) => {
      const to = card.getBoundingClientRect()
      let dx: number
      let dy: number
      let s: number
      if (from.length > 0) {
        const r = from[Math.floor(seeded(i + shown.wave * 7 + 3) * from.length)]
        dx = r.left - to.left
        dy = r.top - to.top
        s = Math.max(0.3, Math.min(2.2, r.width / to.width))
      } else {
        const mx = middle ? middle.left + middle.width / 2 : window.innerWidth / 2
        const my = middle ? middle.top + middle.height / 2 : window.innerHeight / 2
        dx = (mx - (to.left + to.width / 2)) * 0.55
        dy = (my - (to.top + to.height / 2)) * 0.55
        s = 0.6
      }
      card.animate(
        [
          { transform: `translate(${dx}px, ${dy}px) scale(${s})`, opacity: 0 },
          { transform: `translate(${dx}px, ${dy}px) scale(${s})`, opacity: from.length > 0 ? 0.5 : 0, offset: 0.08 },
          { transform: 'translate(0, 0) scale(1)', opacity: 1 },
        ],
        {
          duration: from.length > 0 ? 1100 : 760,
          delay: (from.length > 0 ? 100 : 20) + i * (from.length > 0 ? 40 : 22),
          easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
          fill: 'backwards',
        },
      )
    })
    // Once per wave, not on every resize.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shown.wave])

  // ---------------------------------------------------------- picking
  const inBucket = new Set(bucket.map((one) => one.key))
  const set = (one: SourcePicture, on: boolean) =>
    setBucket((now) => {
      const there = now.some((p) => p.key === one.key)
      if (on && !there) return [...now, one]
      if (!on && there) return now.filter((p) => p.key !== one.key)
      return now
    })
  /** Press and sweep: the first card decides whether the sweep puts in or takes out. */
  const sweep = useRef<boolean | null>(null)
  useEffect(() => {
    const stop = () => {
      sweep.current = null
    }
    window.addEventListener('pointerup', stop)
    window.addEventListener('pointercancel', stop)
    return () => {
      window.removeEventListener('pointerup', stop)
      window.removeEventListener('pointercancel', stop)
    }
  }, [])

  const count = bucket.length
  const allPicked = shown.pictures.length > 0 && shown.pictures.every((one) => inBucket.has(one.key))
  /** Select all: the twenty on the wall, added after what is already in the bucket. */
  const pickAll = () =>
    setBucket((now) => {
      if (allPicked) {
        const here = new Set(shown.pictures.map((one) => one.key))
        return now.filter((one) => !here.has(one.key))
      }
      const there = new Set(now.map((one) => one.key))
      return [...now, ...shown.pictures.filter((one) => !there.has(one.key))]
    })

  return (
    <section className={`mbx${shown.pictures.length > 0 ? ' mbx--wall' : ''}`} ref={stageRef}>
      <Drift pictures={backdrop} ready={driftReady} gone={shown.pictures.length > 0} />
      <div className="mbx__veil" aria-hidden="true" />

      <div className="mbx__ring" ref={ring}>
        {shown.pictures.map((one, i) => {
          const spot = spots[i]
          const n = bucket.findIndex((p) => p.key === one.key)
          return (
            <button
              type="button"
              key={`${shown.wave}-${one.key}`}
              className={`mbx__card${inBucket.has(one.key) ? ' on' : ''}`}
              style={{ left: spot.left, top: spot.top, width: spot.width, height: spot.height }}
              onPointerDown={(event) => {
                if (event.button !== 0) return
                event.preventDefault()
                const on = !inBucket.has(one.key)
                sweep.current = on
                set(one, on)
              }}
              onPointerEnter={() => {
                if (sweep.current !== null) set(one, sweep.current)
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault()
                  set(one, !inBucket.has(one.key))
                }
              }}
              title={one.credit ? `${one.title} · ${one.credit.author} on Unsplash` : one.title}
            >
              <img src={one.thumb} alt={one.title} draggable={false} />
              {n >= 0 ? <span className="mbx__tick">{n === 0 ? 'Lead' : n + 1}</span> : null}
              {one.credit ? <span className="mbx__from">Photo by {one.credit.author} on Unsplash</span> : null}
            </button>
          )
        })}
      </div>

      <div className="mbx__centre">
        {/* One line: where to look, the words, then what to do with what comes back. */}
        <form
          className="mbx__card-ui"
          ref={centreRef}
          onSubmit={(event) => {
            event.preventDefault()
            if (fresh) {
              if (pool.length > SHOWN) another()
            } else search()
          }}
        >
            {/* Where to search: only when there is more than one place. */}
            {places.length > 1 ? (
            <div className="mbx__where">
              <button
                type="button"
                className="mbx__tool mbx__tool--menu"
                aria-haspopup="listbox"
                aria-expanded={menu}
                onClick={() => setMenu((open) => !open)}
                onBlur={() => window.setTimeout(() => setMenu(false), 150)}
              >
                {WHERE_NAME[where]}
                <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
                  <path d="M2 3.5 5 6.5 8 3.5" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              {menu ? (
                <div className="mbx__menu" role="listbox" aria-label="Where to search">
                  {places.map((one) => (
                    <button
                      type="button"
                      key={one}
                      role="option"
                      aria-selected={where === one}
                      className={where === one ? 'on' : ''}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => {
                        setMenu(false)
                        setWhere(one)
                        if (asked) search(asked, one)
                      }}
                    >
                      <span>{WHERE_NAME[one]}</span>
                      <em>{one === 'all' ? 'Visual Language and Unsplash' : one === 'library' ? 'Visual Language' : 'Free photographs'}</em>
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
            ) : null}
            {places.length > 1 ? <span className="mbx__rule" aria-hidden="true" /> : null}
            <svg className="mbx__glass" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <circle cx="7" cy="7" r="4.6" stroke="currentColor" strokeWidth="1.4" />
              <path d="M10.6 10.6 14 14" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
            </svg>
            <input
              className="mbx__input"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Find your inspiration"
              aria-label="Search for pictures"
              autoFocus
            />
            {/* Once these words have been searched, the same place offers the next 20. */}
            {fresh ? (
              <button
                type="button"
                className="mbx__search mbx__search--icon"
                onClick={another}
                disabled={pool.length <= SHOWN}
                title={pool.length > SHOWN ? 'Another 20' : 'No more for these words'}
                aria-label="Another 20"
              >
                <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <path d="M13.2 6.2A5.4 5.4 0 0 0 3.1 5.3M2.8 9.8a5.4 5.4 0 0 0 10.1.9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                  <path d="M3 2.4v3.2h3.2M13 13.6v-3.2H9.8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            ) : (
              <button
                type="submit"
                className={`mbx__search${busy ? ' mbx__search--busy' : ''}`}
                disabled={!query.trim() || busy}
                aria-label={busy ? 'Searching' : 'Search'}
              >
                {/* Searching: the WonderWorks mark dribbling, in colour, in the button. */}
                {busy ? <WwLoader width={46} variant="brand" label="Searching" /> : 'Search'}
              </button>
            )}
            {shown.pictures.length > 0 ? (
              <button type="button" className="mbx__tool" onClick={pickAll}>
                {allPicked ? 'Deselect all' : 'Select all'}
              </button>
            ) : null}
            <button type="button" className="mbx__make" onClick={() => onMake(bucket, asked)} disabled={count === 0}>
              Create mood board{count > 0 ? <span className="mbx__n">{count}</span> : null}
            </button>
        </form>
        {error || (!busy && asked && pool.length === 0) || (!asked && selected.length > 0 && count === 0) ? (
          <div className="mbx__note">
            {error ? (
              error
            ) : asked ? (
              'Nothing found. Try other words.'
            ) : (
              <button type="button" className="mbx__link" onClick={() => onMake(selected.map(fromAsset), '')}>
                Or use the {selected.length} selected in the libraries
              </button>
            )}
          </div>
        ) : null}
      </div>

      <footer className={`mbx__bucket${count > 0 ? ' on' : ''}`}>
        <div className="mbx__picked">
          {bucket.map((one, i) => (
            <button type="button" key={one.key} className="mbx__chip" onClick={() => set(one, false)} title="Take it out">
              <img src={one.thumb} alt={one.title} />
              {i === 0 ? <span>Lead</span> : null}
            </button>
          ))}
        </div>
        <button type="button" className="mbx__clear" onClick={() => setBucket([])}>
          Clear
        </button>
      </footer>
    </section>
  )
}
